import { Link } from "@remix-run/react";
import type { GameSeoContent } from "~/utils/seo-game-content";
import { GAME_SEO_CONFIG, seoPageFontClass } from "~/utils/seo-game-content";
import { getBreadcrumbListJsonLd, getFaqPageJsonLd, getSoftwareApplicationJsonLd } from "~/utils/seo-meta";
import { seoRouteLabel } from "~/utils/seo-routes";

const fontClass = seoPageFontClass;

interface GameSeoPageProps {
  content: GameSeoContent;
}

function slugToTitle(slug: string): string {
  const config = GAME_SEO_CONFIG[slug];
  if (config) return config.title.replace(/\s*\|\s*Prizo$/, "");
  return slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export function GameSeoPage({ content }: GameSeoPageProps) {
  const { slug, title, description, tagline, longDescription, features, benefits, howItWorks, useCases, faq, relatedGames } = content;
  const steps = howItWorks ?? [
    { title: "Customer completes purchase", description: "" },
    { title: "Plays your game on thank-you page", description: "" },
    { title: "Wins a reward and comes back for more", description: "" },
  ];
  const displayTitle = title.replace(/\s*\|\s*Prizo$/, "");
  const pagePath = `/seo/${slug}`;

  return (
    <div className="bg-[#ffffff] w-full">
      {/* JSON-LD structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getSoftwareApplicationJsonLd(displayTitle, description, pagePath)),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbListJsonLd([
              { name: "Home", path: "/" },
              { name: seoRouteLabel(pagePath) ?? displayTitle, path: pagePath },
            ]),
          ),
        }}
      />
      {faq.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getFaqPageJsonLd(faq, pagePath)),
          }}
        />
      )}
      {/* Hero */}
      <div className="seo-section">
        <div className="seo-hero-head">
          <h1 className="seo-h1">
            {displayTitle}
          </h1>
          <p className="seo-lead">
            {tagline}
          </p>
        </div>
        <div className="seo-cta-row">
          <a
            href="https://apps.shopify.com/prizo"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-primary"
          >
            Try For Free
          </a>
          <a
            href="https://calendly.com/nadav-prizo/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-secondary"
          >
            Book a Demo
          </a>
        </div>
      </div>

      {/* Long description */}
      {longDescription && (
        <div className="seo-band-grey">
          <div className="max-w-[720px] mx-auto px-4 md:px-[120px]">
            <div className="seo-body leading-[1.75] space-y-4">
              {longDescription.split(/\n\n+/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* How It Works */}
      <div className="seo-band-violet">
        <div className="seo-wrap">
          <h2 className="seo-h2">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[900px] mx-auto">
            {steps.map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#696cff]/20 flex items-center justify-center mx-auto mb-4">
                  <span className={`${fontClass} font-bold text-[#696cff] text-[18px]`}>{i + 1}</span>
                </div>
                <p className={`${fontClass} font-medium text-[#2c2c2c] text-[16px]`}>{step.title}</p>
                {step.description && (
                  <p className={`${fontClass} text-[#6a6a6a] text-[14px] md:text-[15px] leading-[1.6] mt-2`}>
                    {step.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="seo-section">
        <h2 className="seo-h2">
          Key Features
        </h2>
        <ul className="seo-list">
          {features.map((feature, i) => (
            <li key={i} className="seo-li">
              <span className="text-[#696cff] mt-1">✓</span>
              <span className="seo-body">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Benefits */}
      <div className="bg-[rgba(105,108,255,0.06)] w-full py-12 md:py-20">
        <div className="seo-wrap">
          <h2 className="seo-h2">
            Why Use This Game
          </h2>
          <ul className="seo-list">
            {benefits.map((benefit, i) => (
              <li key={i} className="seo-li">
                <span className="text-[#696cff] font-bold">•</span>
                <span className="seo-body">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Use cases */}
      {useCases && useCases.length > 0 && (
        <div className="seo-section">
          <h2 className="seo-h2">
            When to Use {displayTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1040px] mx-auto">
            {useCases.map((useCase, i) => (
              <div key={i} className="seo-card">
                <h3 className={`${fontClass} font-semibold text-[#2c2c2c] text-[17px] md:text-[18px] mb-2`}>
                  {useCase.title}
                </h3>
                <p className={`${fontClass} text-[#6a6a6a] text-[15px] leading-[1.6]`}>
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <div className="seo-section">
          <h2 className="seo-h2">
            Frequently Asked Questions
          </h2>
          <div className="seo-faq-list">
            {faq.map((item, i) => (
              <div key={i} className="seo-faq-item">
                <h3 className="seo-h3">
                  {item.question}
                </h3>
                <p className="seo-muted">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related games */}
      {relatedGames.length > 0 && (
        <div className="seo-band-violet">
          <div className="seo-wrap">
            <h2 className="seo-h2">
              Explore More Games
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {relatedGames.map((slug) => (
                <Link
                  key={slug}
                  to={`/seo/${slug}`}
                  className="seo-pill"
                >
                  {slugToTitle(slug)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="seo-wrap py-12 md:py-16 text-center">
        <p className={`${fontClass} text-[#6a6a6a] text-[16px] mb-6`}>
          Join brands that use Prizo to gamify checkout and boost revenue.
        </p>
        <a
          href="https://apps.shopify.com/prizo"
          target="_blank"
          rel="noopener noreferrer"
          className="seo-btn-cta"
        >
          Install on Shopify — Free Trial
        </a>
      </div>
    </div>
  );
}
